import React, { useEffect } from 'react';
import Card from '@material-ui/core/Card';
import CardActions from '@material-ui/core/CardActions';
import CardContent from '@material-ui/core/CardContent';
import Icon from '@material-ui/core/Icon';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemText from '@material-ui/core/ListItemText';
import TextField from '@material-ui/core/TextField';
import Button from '@material-ui/core/Button';
import MenuItem from '@material-ui/core/MenuItem';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import { withTranslation, WithTranslation } from 'react-i18next';
import isEmpty from 'lodash/isEmpty';
// Components
import { TooltipButton } from '../tooltip-button';
import LockIcon from '../icons/iconLock';
// Ressources
import FormattingUtils from '../../utils/formattingUtils';
import StringUtils, { REGEX_EMAIL, REGEX_PHONE } from '../../utils/stringUtils';
import { Typobold } from '../typobold';
import { FormatType, FormPickerDatas, ViewEditCell } from '../../local-types';
import { clsx } from '../../utils/htmlUtils';
import ObjectUtils from '../../utils/objectUtils';

interface ViewEditBlockProps extends WithTranslation {
    datas: ViewEditCell[];
    contentProps?: any;
    cardActions?: JSX.Element;
    noActions?: boolean;
    noLock?: boolean;
    loader?: JSX.Element;
    title?: string;
    icon?: string;
    lock?: boolean;
    nbColumn?: number;
    containerStyle?: React.CSSProperties;
    onSubmit?: (editedDatas: any, fullDatas: any, onSuccess: () => void, onError: () => void) => void;
}

const ViewEditBlock = (props: ViewEditBlockProps): JSX.Element => {
    const {
        t,
        contentProps,
        nbColumn,
        noActions,
        noLock,
        title,
        icon,
        lock,
        datas,
        cardActions,
        loader,
        containerStyle,
        onSubmit,
    } = props;
    const classes = useStyles();

    const [modify, setModify] = React.useState<boolean>(false);
    const [editableData, setEditableData] = React.useState<Record<number, any>>({});
    const [formHasError, setFormHasError] = React.useState(false);

    // If we have linked fields we need to keep the data in the state
    const hasLinked = datas.some((item) => item.linked);

    /**
     * LT-876 - back to the values the parent handed down, which is not the same
     * as emptying. `isVisible` resolves a cell's `linked` condition against
     * `editableData` alone, so clearing a block that has linked cells hides
     * every conditional row until the parent sends a new `datas` - and a submit
     * whose reducer changes no state never does.
     */
    const resetEdits = () =>
        setEditableData(hasLinked ? datas.reduce((d, v) => (v.data ? { ...d, [v.id]: v.data } : d), {}) : {});

    //==========================================//
    //=============== UseEffects ===============//
    //==========================================//

    useEffect(() => {
        setFormHasError(!!document.querySelector('.LCError'));
    }, [editableData]);

    useEffect(() => {
        if (hasLinked) {
            resetEdits();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [datas]);

    //=======================================//
    //=============== Actions ===============//
    //=======================================//

    const onChangeModify = () => {
        setModify((prev) => !prev);
    };

    const onInputChange = (key: string, value: any) => {
        setEditableData((prev) => ({ ...prev, [key]: value }));
    };

    const onCancel = () => {
        resetEdits();
        onChangeModify();
    };

    const handleSubmit = (e) => {
        e.preventDefault();
        e.stopPropagation();

        const fullDatas = datas.reduce((acc, item) => {
            acc[item.id] = editableData[item.id] || item.data;
            return acc;
        }, {});

        onSubmit?.(
            editableData,
            fullDatas,
            () => {
                // LT-876 - the cells resolve as `editableData[key] ?? data`, so
                // anything left here outranks what the parent hands down next.
                // Dropping it is what makes a cell show the result rather than
                // the request. It is not the reload: the caller dispatches its
                // refetch without awaiting it, so this runs against the `datas`
                // of the render that submitted.
                resetEdits();
                setModify(false);
            },
            () => {
                // Same on failure, and it matters more: a refused row must fall
                // back to the value still in force, not keep the one that was
                // asked for. Editing stays open. For a block with no linked
                // cells the buffer ends up empty, so Validate disables until
                // the operator picks again; for one with linked cells it is
                // repopulated instead, and re-submitting would resend those
                // values unchanged.
                resetEdits();
            },
        );
    };

    const isVisible = (data: ViewEditCell): boolean => {
        if (!data) {
            return false;
        }

        let res: boolean = true;
        if (data.linked) {
            if (!(data.linked instanceof Array)) {
                data.linked = [data.linked];
            }
            for (const link of data.linked) {
                const { id, value } = link;
                const linkValue = ObjectUtils.getSubValueFromKey(editableData, id);

                if (!Array.isArray(value)) {
                    if (linkValue === undefined && value === false) {
                        continue; // If it's a boolean we accept undefined as false
                    }
                    if (linkValue !== value) {
                        res = false;
                        break;
                    }
                } else {
                    res = value.find((v) => linkValue === v)?.length > 0;
                    if (!res) {
                        break;
                    }
                }
            }
        }
        return res;
    };

    const renderDatas = (): JSX.Element[] => {
        return datas.map((item, index) => {
            const {
                data,
                type,
                optional,
                customValue,
                customStyle,
                placeholder,
                hint,
                inputsAttributs,
                readOnly,
                emptyCell,
                sectionCell,
                hideCell,
                regex,
                format,
                cellType,
                options = item.cellType === 'Boolean' ? [{ id: item.id, name: item.title }] : null,
            } = item;

            const key = item.id || `${index}`;
            const showCell = isVisible(item);

            // console.log('showCell', item.id, showCell);

            const listStyle = {
                flex: nbColumn && nbColumn > 0 ? `${100 / nbColumn}%` : '50%',
            };

            if (hideCell || !showCell) {
                return null;
            }

            if (emptyCell) {
                return <List key={key} className={classes.partnerInfoList} style={listStyle} />;
            }

            /**
             * A band across the whole row, so what follows it reads as one
             * group of cells instead of running into the cells above. It is
             * not a field: nothing here is editable and it takes no part in
             * the submit.
             */
            if (sectionCell) {
                return (
                    <div key={key} className={clsx(classes.sectionCell, index === 0 && classes.sectionCellFirst)}>
                        {item.title && <div className={classes.sectionCellLabel}>{item.title}</div>}
                        {data != null && <div className={classes.sectionCellValue}>{data}</div>}
                    </div>
                );
            }

            let value = editableData?.[key] ?? FormattingUtils.format(data, type, t);
            if (format && typeof value === 'string' && value) {
                value = format(value, 0).value;
            }
            const isDisabled = readOnly || !modify || (!!value && typeof value === 'object' && !cellType);
            const isCheckbox = (cellType === 'Checkbox' && Array.isArray(options)) || cellType === 'Boolean';

            let checkRegex = true;
            if (regex) {
                checkRegex = regex.test(format && typeof value === 'string' ? value.replace(/\s/g, '') : value);
            } else if (type === FormatType.Email) {
                checkRegex = REGEX_EMAIL.test(value);
            } else if (type === FormatType.Phone) {
                checkRegex = REGEX_PHONE.test(value);
            }

            const isError = (value === '' || !checkRegex) && !optional;

            const renderField = () => {
                if (cellType === 'Picker' && Array.isArray(options)) {
                    return (
                        <TextField
                            select
                            fullWidth
                            value={value || ''}
                            placeholder={placeholder}
                            onChange={(e) => onInputChange(key, e.target.value)}
                            variant="outlined"
                            required={!optional}
                            disabled={isDisabled}
                            style={customStyle}
                            inputProps={inputsAttributs}
                        >
                            {options.map((option: FormPickerDatas) => (
                                <MenuItem key={option.id} value={option.id} className={classes.pickerOption}>
                                    {option.name}
                                </MenuItem>
                            ))}
                        </TextField>
                    );
                }

                return (
                    <TextField
                        fullWidth
                        value={value || ''}
                        placeholder={placeholder}
                        onChange={(e) => {
                            if (format) {
                                // Rewritten in place, so the re-render finds the same value and leaves the caret alone
                                const input = e.target as HTMLInputElement;
                                const res = format(
                                    input.value,
                                    input.selectionStart ?? input.value.length,
                                    value,
                                    (e.nativeEvent as InputEvent).inputType,
                                );
                                input.value = res.value;
                                input.setSelectionRange(res.caret, res.caret);
                            }
                            onInputChange(key, e.target.value);
                        }}
                        variant="outlined"
                        required={!optional}
                        disabled={isDisabled}
                        style={customStyle}
                        inputProps={inputsAttributs}
                        // helperText={isError ? t('common:invalide') : hint}
                    />
                );
            };

            return (
                <List
                    key={key}
                    className={clsx(
                        classes.partnerInfoList,
                        !isDisabled && classes.modifying,
                        isError && !isDisabled && clsx(classes.error, 'LCError'),
                        isCheckbox && 'LCCheckbox',
                    )}
                    style={listStyle}
                >
                    <ListItem className={StringUtils.camelCase(item.title)}>
                        {isCheckbox ? (
                            <div className={classes.checkboxGroup}>
                                {options.map((option: FormPickerDatas) => (
                                    <React.Fragment key={option.id}>
                                        <label className={clsx(classes.checkboxLabel, isDisabled && 'LCDisabled')}>
                                            <input
                                                type="checkbox"
                                                disabled={isDisabled}
                                                checked={Array.isArray(value) ? value?.includes(option.id) : !!value}
                                                onChange={(e) => {
                                                    if (cellType === 'Boolean') {
                                                        onInputChange(key, !value);
                                                    } else {
                                                        const newValue = value || [];
                                                        const updatedValue = e.target.checked
                                                            ? [...newValue, option.id]
                                                            : newValue.filter((val: string) => val !== option.id);
                                                        onInputChange(key, updatedValue);
                                                    }
                                                }}
                                            />
                                            {option.name}
                                        </label>
                                        {hint && (
                                            <span className={classes.value} style={{ marginLeft: 8 }}>
                                                {hint}
                                            </span>
                                        )}
                                    </React.Fragment>
                                ))}
                            </div>
                        ) : (
                            <>
                                <ListItemText
                                    className={classes.partnerInfoLabel}
                                    primaryTypographyProps={{
                                        className: classes.flexCenter,
                                    }}
                                >
                                    <div className={classes.formLabel}>{item.title}</div>
                                    {!noLock && readOnly && (
                                        <TooltipButton title={t('common:lock')}>
                                            <LockIcon />
                                        </TooltipButton>
                                    )}
                                </ListItemText>
                                <ListItemText className={clsx(classes.data, 'LCData')}>
                                    {value && typeof value === 'object' ? (
                                        <div className="LCDataObject">{value}</div>
                                    ) : (
                                        renderField()
                                    )}
                                </ListItemText>
                            </>
                        )}
                    </ListItem>
                </List>
            );
        });
    };

    const WrapperElement = modify ? 'form' : 'div';
    const showActions = !noActions && !!onSubmit;

    return (
        <div
            {...contentProps}
            className={clsx(classes.rootViewEditBlock, 'LCRootViewEditBlock')}
            style={containerStyle ?? { width: '100%' }}
        >
            <Card className={classes.card}>
                <WrapperElement onSubmit={handleSubmit}>
                    <div className={classes.cardHeader}>
                        <div className={classes.flexCenter}>
                            {title && <Typobold>{title}</Typobold>}
                            {icon && (
                                <Icon color="primary" className={classes.icon}>
                                    {icon}
                                </Icon>
                            )}
                            {lock && (
                                <TooltipButton title={t('common:lock')}>
                                    <Icon color="primary" className={classes.icon}>
                                        lock
                                    </Icon>
                                </TooltipButton>
                            )}
                        </div>
                        {showActions && (
                            <div className={classes.cardHeaderActions}>
                                {modify && (
                                    <Button
                                        color="default"
                                        variant="contained"
                                        className={classes.buttonGrey}
                                        onClick={onCancel}
                                    >
                                        {t('common:cancel')}
                                    </Button>
                                )}
                                <Button
                                    color="secondary"
                                    variant="contained"
                                    type={modify ? 'submit' : 'button'}
                                    onClick={modify ? null : onChangeModify}
                                    disabled={modify && (isEmpty(editableData) || formHasError)}
                                >
                                    {t(modify ? 'common:validate' : 'common:edit')}
                                </Button>
                            </div>
                        )}
                    </div>
                    <CardContent className={classes.cardContent}>
                        {renderDatas()}
                        {loader}
                    </CardContent>
                    {cardActions && <CardActions className={classes.rightActions}>{cardActions}</CardActions>}
                </WrapperElement>
            </Card>
        </div>
    );
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        rootViewEditBlock: {
            marginTop: theme.spacing(3),
            '& .MuiFormControl-root.MuiTextField-root': {
                '& .MuiFormLabel-root.MuiInputLabel-root.MuiInputLabel-formControl[data-shrink=false]': {
                    whiteSpace: 'nowrap',
                    width: '100%',
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                },
            },
        },
        modifying: {
            '& .MuiTypography-body1': {
                color: '#2196F3',
            },
            '& .LCData': {
                borderBottom: '1px solid #2196F3',
            },
        },
        error: {
            '& .MuiTypography-body1': {
                color: theme.palette.error.main,
            },
            '& .LCData': {
                borderBottom: `1px solid ${theme.palette.error.main}`,
            },
        },
        card: {
            display: 'flex',
            flexDirection: 'column',
            gap: 20,
            padding: '20px 28px',
            '& .MuiCardContent-root': {
                paddingBottom: 0,
            },
        },
        cardHeader: {
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 20,
        },
        cardHeaderActions: {
            display: 'flex',
            gap: 20,
        },
        cardContent: {
            display: 'flex',
            flexWrap: 'wrap',
            padding: 0,
        },
        formLabel: {
            fontSize: 12,
            fontWeight: 500,
        },
        sectionCell: {
            flex: '1 0 100%',
            display: 'flex',
            alignItems: 'flex-start',
            gap: 12,
            marginTop: 28,
            marginBottom: 4,
            padding: '8px 12px',
            borderRadius: 4,
            borderLeft: `3px solid ${theme.palette.secondary.main}`,
            backgroundColor: '#F4F6F8',
        },
        sectionCellFirst: {
            marginTop: 0,
        },
        sectionCellLabel: {
            fontSize: 12,
            fontWeight: 500,
            lineHeight: '24px',
            letterSpacing: 0.4,
            textTransform: 'uppercase',
            color: theme.palette.primary.dark,
        },
        sectionCellValue: {
            display: 'flex',
            flexDirection: 'column',
            gap: 2,
            fontSize: 16,
            fontWeight: 600,
            lineHeight: '24px',
        },
        buttonGrey: {
            backgroundColor: '#E0E0E0',
            '&:hover': {
                backgroundColor: '#E0E0E0BB',
            },
        },
        data: {
            borderBottom: '1px dotted #0000006B',
            '& .MuiOutlinedInput-notchedOutline': {
                border: 'none',
            },
            '& .MuiOutlinedInput-input': {
                padding: '6px 0px',
                fontSize: 16,
                fontWeight: 400,
                color: theme.palette.text.primary,
                '&:disabled, &.Mui-disabled': {
                    color: theme.palette.text.secondary,
                },
            },
            '& .LCDataObject': {
                padding: '6px 0px',
                fontSize: 16,
                fontWeight: 400,
                color: theme.palette.text.secondary,
            },
        },
        value: {
            fontSize: 16,
            fontWeight: 400,
            color: theme.palette.text.secondary,
        },
        icon: {
            marginLeft: 8,
        },
        partnerInfoList: {
            display: 'flex',
            '& li ': {
                flex: '1 0 50%',
                flexDirection: 'column',
                alignItems: 'inherit',
                padding: '8px 16px 8px 0px',
            },
            '& .MuiListItemText-root': {
                flex: 'initial',
                margin: theme.spacing(0, 1),
            },
            '&.LCCheckbox': {
                flex: '1 0 100% !important',
                '& input': {
                    width: 18,
                    height: 18,
                    cursor: 'pointer',
                    '&:disabled': {
                        cursor: 'default',
                    },
                },
                '& label.LCDisabled': {
                    color: theme.palette.text.secondary,
                },
            },
        },
        partnerInfoLabel: {
            color: theme.palette.primary.dark,
        },
        rightActions: {
            justifyContent: 'flex-end',
        },
        flexCenter: {
            display: 'flex',
            alignItems: 'center',
        },
        pickerOption: {
            margin: '4px 0px',
            padding: 4,
            '&:hover': {
                cursor: 'pointer',
                backgroundColor: '#0000002B',
            },
        },
        checkboxGroup: {
            display: 'flex',
            flexDirection: 'row',
            gap: '12px',
        },
        checkboxLabel: {
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: '12px',
            fontSize: '16px',
            '&.LCDisabled': {
                cursor: 'default',
            },
        },
    }),
);

export default withTranslation(['common'])(ViewEditBlock);
